import { useCallback, useLayoutEffect, useState, type RefObject } from 'react';

import { useUserBi } from '../../telemetry/hooks/useUserBi/useUserBi.js';
import {
  ActionOutcome,
  EntityType,
  getOperationActionScenario,
  OperationType,
  PanelRegion,
} from '../../telemetry/userbi.js';
import type { PanelType } from '../../telemetry/userbi.js';

export const REPORT_DOWNLOAD_REQUEST = 'aistudio:download-report';

const DOWNLOAD_URL_LIFETIME_MS = 1000;

export function useReportDownload({
  panelType,
  iframeRef,
  html,
  runId,
}: {
  panelType: PanelType;
  iframeRef: RefObject<HTMLIFrameElement>;
  html: string | null;
  runId: string | null;
}) {
  const [error, setError] = useState<string | null>(null);
  const { recordAction } = useUserBi();

  const download = useCallback(
    (html: string, runId: string) => {
      setError(null);
      let url: string | undefined;
      let anchor: HTMLAnchorElement | undefined;
      try {
        const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
        url = URL.createObjectURL(blob);
        anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = `evaluation-report-${runId.replace(/[^a-zA-Z0-9_-]/g, '_')}.html`;
        anchor.hidden = true;
        document.body.appendChild(anchor);
        anchor.click();
      } catch (error) {
        setError(
          `Unable to download report: ${error instanceof Error ? error.message : 'Please try again.'}`,
        );
        return;
      } finally {
        anchor?.remove();
        if (url !== undefined) {
          const downloadUrl = url;
          // Let the browser start consuming the blob before releasing it.
          window.setTimeout(
            () => URL.revokeObjectURL(downloadUrl),
            DOWNLOAD_URL_LIFETIME_MS,
          );
        }
      }

      recordAction({
        panelType,
        panelRegion: PanelRegion.Header,
        actionScenario: getOperationActionScenario(
          EntityType.EvaluationRun,
          OperationType.Download,
        ),
        actionScenarioType: OperationType.Download,
        actionGesture: 'click',
        actionOutcome: ActionOutcome.Requested,
        data: { 'Usage.Format': 'html' },
      });
    },
    [panelType, recordAction],
  );

  useLayoutEffect(() => {
    setError(null);
    if (html === null || runId === null) return;
    const onMessage = (event: MessageEvent<unknown>) => {
      // Sandboxed srcDoc has an opaque origin: identify the frame, not "null".
      const source = iframeRef.current?.contentWindow;
      if (
        source &&
        event.source === source &&
        event.data === REPORT_DOWNLOAD_REQUEST
      ) {
        download(html, runId);
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [download, html, iframeRef, runId]);

  return { error };
}
